import {I18n} from 'common/translator/i18n';
import {FontFaceDto} from 'types/paymentTerms';
import {getCookie, getQueryParam} from 'common/utils';

import {Cart} from '../payButton/types';
import {
  AvailableLoanType,
  ProductFormElement,
  MinIneligibleMessageType,
  VariantModalDetails,
  InstallmentPlan,
  SamplePlan,
  ModalText,
} from '../../types';

import {
  BANNER_UNSUPPORTED_FOR_THEME_MESSAGE,
  SHARED_MODAL_STYLES,
} from './constants';

interface VariantInputElement extends HTMLInputElement {}

export interface InstallmentsModal {
  rootContainer: Node;
  focusLockTarget: HTMLElement;
}

/**
 * @returns {number} The variant ID, if we could extract it from the URL.
 */
export function extractVariantIdFromURL(): number | undefined {
  const params = new URL(document.location.href).searchParams;
  const variantParam = params.get('variant');
  return variantParam ? Number(variantParam) : undefined;
}

/**
 * @param {number} price of the item.
 * @returns {string} formatted price according to en-US locale.
 */
export function formatPrice(price: number) {
  const options = {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  };

  return new Intl.NumberFormat('en-US', options).format(price);
}

/**
 * @returns {string?} element selector for the subtotal of a given theme, or null.
 */
export function cartSubtotalSelectorsForTheme() {
  switch ((window as any).Shopify?.theme?.name?.toLowerCase()) {
    case 'boundless':
      return '.cart__subtotal';
    case 'brooklyn':
      return '.cart__subtotal';
    case 'dawn':
      return '.totals__subtotal-value, .sections.cart.new_subtotal';
    case 'debut':
      return '.cart-subtotal__price';
    case 'express':
      return '.cart__subtotal, .cart-drawer__subtotal-value';
    case 'minimal':
      return '.h5.cart__subtotal-price';
    case 'narrative':
      return '.cart-subtotal__price, .cart-drawer__subtotal-number';
    case 'simple':
      return '.cart__subtotal.h3';
    case 'supply':
      return '.h1.cart-subtotal--price';
    case 'venture':
      return '.CartSubtotal';
  }
  return null;
}

/**
 * Prints a warning if banner is not supported for the current theme.
 */
export function confirmCartSubtotalAttribute() {
  const elementWithCartSubtotalAttribute = document.querySelector(
    '[data-cart-subtotal]',
  );

  if (!elementWithCartSubtotalAttribute) {
    // eslint-disable-next-line no-console
    console.warn(BANNER_UNSUPPORTED_FOR_THEME_MESSAGE);
  }
}

/**
 * @param {ShadowRoot?} shadowRoot Shadow DOM of the current element.
 * @returns {string} hex string for the background color.
 */
export function inferBackgroundColor(shadowRoot: ShadowRoot | null) {
  const whiteBackgroundColor = '#FFF';

  if (!shadowRoot) {
    return whiteBackgroundColor;
  }

  const paymentTermsSectionColor =
    shadowRoot.host.parentElement &&
    getComputedStyle(shadowRoot.host.parentElement)
      .getPropertyValue('--payment-terms-background-color')
      ?.trim();

  const paymentTermsBodyColor = getComputedStyle(document.documentElement)
    .getPropertyValue('--payment-terms-background-color')
    ?.trim();

  // Ideally we would like to have a single CSS variable to represent the background color,
  // but we have partners and merchants already using the --color-body variable on their
  // document root so we shouldn't remove it.
  const shopBackgroundColor = getComputedStyle(document.documentElement)
    .getPropertyValue('--color-body')
    ?.trim();

  // The current priority order is:
  // 1. Check the parent element of our banner for --payment-terms-background-color
  // 2. Check the document root for --payment-terms-background-color
  // 3. Check the document root for --color-body
  const backgroundColor =
    paymentTermsSectionColor || paymentTermsBodyColor || shopBackgroundColor;

  // Assume our background is white if we couldn't infer a background color or CSS
  // doesn't support this background color.
  if (!backgroundColor || !CSS.supports('color', backgroundColor)) {
    return whiteBackgroundColor;
  }

  return backgroundColor;
}

/**
 * @param {string} price A price with currency symbol.
 * @returns {number} The same price returned as a number.
 */
export function convertPriceToNumber(price: string) {
  return parseFloat(price.replace(/[^0-9.]/g, ''));
}

/**
 * @param {HTMLFormElement | null} productForm The overarching product form element found on the page
 * @returns {number} The product quantity selected by the user
 */
export function getProductQuantityFromDOM(
  productForm?: HTMLFormElement | null,
): number {
  // If no productForm exists at all, default to 1
  if (!productForm) {
    return 1;
  }
  // Handles most existing stores
  const elements = productForm.elements as ProductFormElement;
  const quantityElement = elements.quantity;
  if (quantityElement) {
    return Number(quantityElement.value);
  }

  // For Dawn and other free themes, installments form and product form are separate
  // Need to getAttribute('id'), as there is a hidden input with name="id"
  const productFormId = productForm.getAttribute('id');
  const sectionId = productFormId?.replace('product-form-installment-', '');
  const quantityElementById = document.getElementById(`Quantity-${sectionId}`);

  if (quantityElementById) {
    return Number((quantityElementById as HTMLInputElement).value);
  }

  // If not able to find quantityInput, default to 1
  return 1;
}

/**
 * Try to extract the new variant ID from the form or the page URL.
 * @param {HTMLFormElement} productForm The overarching product form element found on the page
 * @returns {number} The product variant ID selected by the user
 */
export function getNewProductVariantId(
  productForm: HTMLFormElement,
): number | undefined {
  const variantIdElement = (productForm.querySelector('select[name^="id"]') ||
    productForm.querySelector('[name^="id"]')) as VariantInputElement | null;

  if (variantIdElement) {
    return Number(variantIdElement.value);
  } else {
    return extractVariantIdFromURL();
  }
}

/**
 * @param {AvailableLoanType} loanTypes the available loan types
 * @returns {MinIneligibleMessageType} the ineligible message type when the minimum price is not met
 */
export function getMinIneligibleMessageType(
  loanTypes?: AvailableLoanType[],
): MinIneligibleMessageType {
  if (!loanTypes) {
    return MinIneligibleMessageType.SplitPay;
  }

  if (
    (loanTypes.includes(AvailableLoanType.Interest) &&
      !loanTypes.includes(AvailableLoanType.SplitPay)) ||
    loanTypes.includes(AvailableLoanType.ZeroPercent)
  ) {
    return MinIneligibleMessageType.Monthly;
  }

  return MinIneligibleMessageType.SplitPay;
}

/**
 *
 * @param {I18n} i18n instance of the i18n class.
 * @returns {string} partnership disclaimer HTML with translated copy
 */
export function getPartnershipDisclaimer(i18n: I18n): string {
  const affirmLogo =
    '<svg role="img" aria-labelledby="shopify-payment-terms-modal-affirm" class="affirm-logo-inline affirm-logo"><use xlink:href="#affirm" /></svg>';

  const partnership = i18n.isEnglish()
    ? i18n.translate('modal.partnership', {
        affirmLogo,
      })
    : i18n.translate('modal.partnership_disclaimer', {
        affirmLogo,
      });

  return partnership;
}

/**
 * @param {I18n} i18n instance of the i18n class.
 * @param {number} numberOfPlans number of plans to show in the modal
 * @param {string} priceWithoutInterest Total price of the cart or product, without interest.
 * @returns {ModalText} contains two keys, one with legal copy and one with a subtitle for the modal contents
 */
export function getDynamicPDPText(
  i18n: I18n,
  numberOfPlans: number,
  priceWithoutInterest: string,
): ModalText {
  const subTitle = i18n.translate('modal.subtitle.dynamic_pdp', {
    count: numberOfPlans,
    priceWithoutInterest,
  });
  const legalCopy = i18n.translate('modal.legal.dynamic_pdp');

  return {
    subTitle,
    legalCopy,
  };
}

/**
 * @param {InstallmentPlan[]} installmentPlans array of installment plans to be displayed as a list in the modal.
 * @param {string} priceWithoutInterest Total price of the cart or product, without interest.
 * @returns {SamplePlan[]} plans formatted to include data relevant per term that needs to be displayed.
 */
export function getFormattedSamplePlans(
  installmentPlans: InstallmentPlan[],
  priceWithoutInterest: string,
): SamplePlan[] {
  return installmentPlans.map(
    ({pricePerTerm, apr, numberOfPaymentTerms, loanType}) => {
      const totalPriceWithInterestNumber =
        convertPriceToNumber(pricePerTerm) * numberOfPaymentTerms;
      const priceWithoutInterestNumber =
        convertPriceToNumber(priceWithoutInterest);

      // Otherwise rounding being done throughout flow results in very small +ve/-ve interest values when apr is 0
      const interest = formatPrice(
        apr === 0
          ? 0
          : totalPriceWithInterestNumber - priceWithoutInterestNumber,
      );
      const totalPriceWithInterest = formatPrice(
        apr === 0 ? priceWithoutInterestNumber : totalPriceWithInterestNumber,
      );

      return {
        pricePerTerm,
        apr,
        numberOfPaymentTerms,
        interest,
        totalPriceWithInterest,
        loanType,
      };
    },
  );
}

/**
 * @param {I18n} i18n instance of the i18n class.
 * @param {string} subTitle shown after the header in the modal.
 * @param {string} legalCopy shown at the bottom of the modal.
 * @param {SamplePlan[]} samplePlans array of plans formatted to include data relevant per
 *   term that needs to be displayed.
 * @returns {string} full HTML content.
 */
export function getDynamicPDPTemplateContents(
  i18n: I18n,
  subTitle: string,
  legalCopy: string,
  samplePlans: SamplePlan[],
): string {
  const title = i18n.translate('modal.title');

  const eligibilityLabel = i18n.translate(
    'modal.sample_plan_contents.check_eligibility',
  );
  const interestLabel = i18n.translate('modal.sample_plan_contents.interest');
  const totalLabel = i18n.translate('modal.sample_plan_contents.total');
  const processingLabel = i18n.translate(
    'modal.sample_plan_contents.processing',
  );
  const processingTimeLabel = i18n.translate(
    'modal.sample_plan_contents.processing_time',
  );

  const partnership = getPartnershipDisclaimer(i18n);
  const newWindowLabel = i18n.translate('modal.new_window');
  const closeLabel = i18n.translate('modal.close');

  return `
  <style>
    ${SHARED_MODAL_STYLES}

    shop-pay-button[disabled] {
      pointer-events: none;
    }

    #shopify-payment-terms-modal header {
      display: flex;
      flex-direction: column;
    }

    #shopify-payment-terms-modal h1 {
      display: flex;
      flex-direction: column;
      color: rgb(18, 18, 18)
    }

    #shopify-payment-terms-modal p {
      color: rgba(18, 18, 18, 0.7)
    }

    #shopify-payment-terms-modal #header-icons {
      display: flex;
      width: 100%;
      justify-content: space-between;
      align-items: baseline;
      margin-bottom: 16px;
    }

    #shopify-payment-terms-modal .btn__close {
      margin: 0;
      background: transparent;
      border: none;
      -webkit-font-smoothing: inherit;
      appearance: button;
      cursor: pointer;
    }

    #shopify-payment-terms-modal h1 {
      font-size: 30px;
      font-weight: 600;
      line-height: 120%;
      margin: 0;
      max-width: 100%;
      font-family: inherit;
      letter-spacing: 0.02em;
    }

    #shopify-payment-terms-modal .tagline {
      margin: 0 0 16px 0;
      line-height: 24px;
    }
    #shopify-payment-terms-modal .list {
      background-color: #F5F5F5;
      border-radius: 5px;
      border: 1px solid #D9D9D9;
      padding: 17px 21px;
    }
    #shopify-payment-terms-modal .list-item {
      display: flex;
      justify-content: space-between;
      flex-direction: column;
      padding: 11px 0;
      color: #707070;
      border-bottom: 1px solid #D9D9D9;
    }

    #shopify-payment-terms-modal .list-item:first-child {
      padding-top: 0;
    }

    #shopify-payment-terms-modal .list-item:last-child {
      border-bottom: none;
      padding-bottom: 0;
    }

    #shopify-payment-terms-modal .list-item__price-per-term {
      font-size: 18px;
      font-weight: 600;
    }

    #shopify-payment-terms-modal .list-item__frequency {
      font-size: 14px;
      font-weight: 600;
    }

    #shopify-payment-terms-modal .list-item__payment-length {
      padding: 2px 0px;
      background: #F0F2F4;
      border-radius: 4px;
    }

    #shopify-payment-terms-modal .list-item__number-of-terms {
      font-size: 14px;
    }

    #shopify-payment-terms-modal .list-item__interest-details-container {
      display: flex;
      flex-direction: column;
      row-gap: 9px;
    }

    #shopify-payment-terms-modal .list-item__interest-detail-label {
      font-size: 12px;
      line-height: 18px;
    }

    #shopify-payment-terms-modal .list-item__interest-detail-value {
      font-size: 12px;
      line-height: 18px;
    }

    #shopify-payment-terms-modal .list-item__financial-container {
      display: flex;
      justify-content: space-between;
      margin-bottom: 9px;
    }

    #shopify-payment-terms-modal .list-item__amount-per-interval {
      display: flex;
      align-items: baseline;
      line-height: 26px;
    }

    #shopify-payment-terms-modal .list-item__plan-total {
      font-weight: 600;
    }

    #shopify-payment-terms-modal .list-item__interest-detail {
      display: flex;
      flex-direction: row;
      margin-right: 12px;
      align-content: center;
      justify-content: space-between;
      width: 100%;
    }

    #shopify-payment-terms-modal .navigation-buttons {
      display: flex;
      flex-direction: column;
      align-items: center;
      margin-top: 16px;
    }

    #shopify-payment-terms-modal .shop-status-indicator-loading {
      width: 100%;
      height: 42px;
    }

    #shopify-payment-terms-modal .check_eligibility {
      margin: 5px 0;
      font-weight: 300;
      font-size: 12px;
      text-align: center;
    }

    #shopify-payment-terms-modal .help_text {
      margin-top: 32px;
      font-size: 12px;
    }

    .shop-modal-content-processing {
      display: flex;
      align-items: center;
      position: absolute;
      top: 0;
      bottom: 0;
      right: 0;
      left: 0;
      padding: 2em;
      flex-direction: column;
      justify-content: center;

      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica,
    Arial, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol';
    }

    @keyframes fadeIn {
      0% { opacity: 0; }
      100% { opacity: 1; }
    }

    .shop-modal-content-processing:not(.prequal-hidden-state) {
      animation: fadeIn 1s;
    }

    .shop-modal-content-processing > div {
      margin-top: 20px;
      text-align: center;
    }

    .shop-modal-content-processing .shop-modal-content-processing-loading-container {
      height: 100%;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
    }

    .shop-modal-content-processing-loading-container > div {
      margin-top: 20px;
    }

    .shop-modal-content-processing p {
      margin: 0;
    }

    .shop-modal-content-processing p:first-child {
      color: black;
    }

    .shop-modal-content-processing p:last-child {
      font-size: 12px;
      color: rgba(0, 0, 0, 0.6);
    }

    .hidden-navigation-buttons {
      display: none;
    }

    .prequal-hidden-state {
      display: none !important;
    }

    .modal-wrapper.opaque-hidden {
      opacity: 0;
      height: 500px;
      overflow: hidden;
    }
  </style>
  <div class="shop-modal-content-processing prequal-hidden-state">
    <div class="shop-modal-content-processing-loading-container">
      <div>
        <p>${processingLabel}</p>
        <p>${processingTimeLabel}</p>
      </div>
    </div>
    <div>
      ${logoElements(partnership)}
      ${logoSvgs()}
    </div>
  </div>
  <div class="shop-modal-feature-iframe-wrapper prequal-hidden-state"></div>
  <div class="continue-to-checkout-button prequal-hidden-state"></div>
  <div
    id="shopify-payment-terms-modal"
    role="dialog"
    aria-modal="true"
    aria-labelledby="shopify-payment-terms-modal-heading"
    tabindex="-1">
    <section class="modal-wrapper">
      <header>
        <div id="header-icons">
          <h1 id="shopify-payment-terms-modal-heading">${title}</h1>
          <button type="button" class="btn__close btn__close--icon" aria-label="${closeLabel}">
            <svg class="close__icon" aria-hidden="true"><use xlink:href="#close" /></svg>
          </button>
        </div>
      </header>
      <div hidden id="shopify-payment-terms-modal-warning-text">${newWindowLabel}</div>
      <p class="tagline">${subTitle}</p>
      <ul class="list">
        ${samplePlans
          .map(
            ({
              pricePerTerm,
              apr,
              numberOfPaymentTerms,
              interest,
              totalPriceWithInterest,
              loanType,
            }) => {
              // const aprLabel = i18n.translate('modal.sample_plan_contents.apr');

              const aprLabel = i18n.translate(
                'modal.sample_plan_contents.apr',
                {
                  aprAmount: apr,
                },
              );
              const paymentFrequency =
                loanType === AvailableLoanType.SplitPay
                  ? i18n.translate(
                      'modal.sample_plan_contents.split_pay_frequency',
                      {pricePerTerm},
                    )
                  : i18n.translate(
                      'modal.sample_plan_contents.other_frequency',
                      {pricePerTerm},
                    );

              const numberOfTerms =
                loanType === AvailableLoanType.SplitPay
                  ? i18n.translate(
                      'modal.sample_plan_contents.split_pay_number_of_terms',
                      {numberOfTerms: numberOfPaymentTerms * 2},
                    )
                  : i18n.translate(
                      'modal.sample_plan_contents.other_number_of_terms',
                      {numberOfTerms: numberOfPaymentTerms},
                    );
              return `
          <li class="list-item">
            <div class="list-item__financial-container">
              <span class="list-item__amount-per-interval">
                ${paymentFrequency}
                <span class="list-item__number-of-terms">${numberOfTerms}</span>
              </span>
            </div>
            <div class="list-item__interest-details-container">
              <div class="list-item__interest-detail">
                <span class="list-item__interest-detail-label list-item__apr-amount">${interestLabel} (${apr}% ${aprLabel})</span>
                <span class="list-item__interest-detail-value list-item__interest-amount">${interest}</span>
              </div>
              <div class="list-item__interest-detail">
                <span class="list-item__interest-detail-label">${totalLabel}</span>
                <span class="list-item__interest-detail-value list-item__plan-total">${totalPriceWithInterest}</span>
              </div>
            </div>
          </li>
        `;
            },
          )
          .join('')}
      </ul>
      <div class="navigation-buttons"></div>
      <p class="check_eligibility">${eligibilityLabel}</p>
      <p id="eligibility-approval" class="help_text">
        <small>
          ${legalCopy}
        </small>
      </p>
      <footer>
        ${logoElements(partnership)}
      </footer>
      ${logoSvgs()}
    </section>
  </div>`;
}

/**
 * @param {string} storeUrl The URL of the store
 * @param {string} modalToken A token generated by the modal to uniquely identify the modal and actions taken by the user.
 * @param {string} buttonText The text to display on the button
 * @param {string} variantInfo Variant information in the shape of `variantId:quantity`, is undefined when opened on the cart page
 * @param {Cart} cart Details related to the cart that the modal is opened from (undefined on product page)
 * @returns {string} The navigation buttons and associated styling formatted as a string
 */
export function getNavigationButtons(
  storeUrl: string,
  modalToken: string,
  buttonText: string,
  variantInfo?: VariantModalDetails,
  cart?: Cart,
): string {
  const variantsAttribute =
    variantInfo === undefined
      ? ''
      : `variants="${variantInfo.idQuantityMapping}" ${
          variantInfo.available === false ? 'disabled' : ''
        }`;

  const cartAttribute =
    cart === undefined
      ? ''
      : `cart=${encodeURIComponent(
          JSON.stringify({
            currency: cart.currency,
            items: cart.items,
            token: cart.token,
          }),
        )}`;

  return `
   <shop-pay-button style="width: 100%; --shop-pay-button-width: auto;" store-url="${storeUrl}" ${variantsAttribute} button-text="${buttonText}" payment-option="shop_pay_installments" source="installments_modal" source-token="${modalToken}" ${cartAttribute}></shop-pay-button>
 `;
}

/**
 * @param {string} partnership Affirm disclaimer HTML
 * @returns {string} HTML to display the Shop Pay logo followed by the Affirm logo
 */
export function logoElements(partnership: string): string {
  return `
  <div class="shop-pay-logo-wrapper">
    <shop-pay-logo role="img" aria-label="Shop Pay" size="large"></shop-pay-logo>
  </div>
  <div class="affirm-text">
    <small>
      ${partnership}
    </small>
  </div>`;
}
/**
 *  @returns {string} SVG elements to display Shop Pay and Affirm logo
 */
export function logoSvgs(): string {
  return `<svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" style="display: none;">
  <symbol id="fees-icon" viewBox="0 0 21 18" fill="none">
    <path d="M18.5 10.5474C18.5 15.267 14.5995 19.1236 9.75 19.1236C4.90048 19.1236 1 15.267 1 10.5474C1 5.82767 4.90048 1.97107 9.75 1.97107C14.5995 1.97107 18.5 5.82767 18.5 10.5474Z" stroke="#0B0B0C" stroke-width="2"/>
    <path d="M11.4875 8.06938C11.8819 8.456 12.515 8.4497 12.9016 8.05531C13.2882 7.66092 13.2819 7.02778 12.8875 6.64117L11.4875 8.06938ZM10.0408 6.55725V7.55725V6.55725ZM7.1335 12.4337C6.70617 12.0838 6.07613 12.1466 5.72626 12.5739C5.37639 13.0013 5.43917 13.6313 5.8665 13.9812L7.1335 12.4337ZM12.1875 7.35527C12.8875 6.64117 12.8872 6.64088 12.887 6.6406C12.8869 6.6405 12.8866 6.64021 12.8864 6.64001C12.8859 6.63961 12.8855 6.6392 12.8851 6.63878C12.8842 6.63793 12.8833 6.63703 12.8823 6.63609C12.8804 6.63419 12.8782 6.6321 12.8758 6.62982C12.871 6.62526 12.8654 6.61993 12.8589 6.61388C12.8459 6.6018 12.8294 6.58683 12.8096 6.5694C12.7699 6.53459 12.7164 6.48973 12.6497 6.43832C12.5166 6.33585 12.328 6.20519 12.0884 6.07602C11.6099 5.81808 10.9106 5.55725 10.0408 5.55725V7.55725C10.5036 7.55725 10.8777 7.69544 11.1394 7.83651C11.2698 7.90684 11.3681 7.97569 11.4293 8.02285C11.4597 8.04625 11.4802 8.06374 11.4903 8.07257C11.4953 8.07696 11.4976 8.07914 11.4972 8.07874C11.497 8.07854 11.4961 8.07769 11.4944 8.07615C11.4936 8.07538 11.4927 8.07443 11.4915 8.07331C11.4909 8.07274 11.4903 8.07214 11.4896 8.07148C11.4893 8.07116 11.4889 8.07082 11.4886 8.07047C11.4884 8.07029 11.4881 8.07002 11.488 8.06993C11.4877 8.06966 11.4875 8.06938 12.1875 7.35527ZM10.0408 5.55725C8.12515 5.55725 6.90378 6.0365 6.19464 6.83562C5.49081 7.62876 5.5 8.51566 5.5 8.77398L7.5 8.77398C7.5 8.58897 7.50919 8.3675 7.69057 8.1631C7.86664 7.96469 8.41567 7.55725 10.0408 7.55725V5.55725ZM5.5 8.77398C5.5 10.0149 6.8138 11.5474 10.0408 11.5474V9.54737C7.35373 9.54737 7.5 8.41981 7.5 8.77398L5.5 8.77398ZM10.0408 11.5474C11.3127 11.5474 11.7267 11.9199 11.8526 12.072C11.9294 12.1647 11.9638 12.2521 11.9797 12.3098C11.9877 12.3388 11.9908 12.3595 11.9919 12.3681C11.9924 12.3721 11.9925 12.3743 11.9927 12.376C11.9927 12.3767 11.9928 12.3777 11.9929 12.3796C11.993 12.3801 11.9934 12.3859 11.9937 12.3909C11.9938 12.3916 11.9953 12.4128 11.9983 12.4379C11.9991 12.4448 12.004 12.4884 12.0163 12.5424C12.0206 12.5613 12.0384 12.6404 12.081 12.7343C12.1072 12.7876 12.1909 12.9199 12.2529 12.9949C12.374 13.1073 12.744 13.2896 12.9915 13.3208V11.3208C13.239 11.3519 13.609 11.5342 13.7301 11.6466C13.7921 11.7215 13.8758 11.8538 13.9019 11.907C13.9445 12.0008 13.9622 12.0798 13.9664 12.0985C13.9786 12.152 13.9835 12.195 13.9842 12.201C13.9859 12.2154 13.9869 12.2264 13.9873 12.2305C13.9877 12.2355 13.988 12.2393 13.9881 12.2405C13.9882 12.2421 13.9883 12.2437 13.988 12.2392C13.9878 12.2363 13.9874 12.2314 13.987 12.2257C13.9852 12.2014 13.9821 12.1655 13.9765 12.121C13.9655 12.0326 13.9455 11.9148 13.9076 11.7775C13.8317 11.5027 13.6833 11.1468 13.3928 10.7961C12.7843 10.0615 11.7261 9.54737 10.0408 9.54737V11.5474ZM12.9915 13.3208C12.7152 13.2818 12.3392 13.0787 12.2329 12.9723C12.1834 12.9097 12.1152 12.8024 12.0934 12.7604C12.0576 12.6873 12.0393 12.6263 12.0345 12.6106C12.0212 12.5665 12.014 12.5313 12.0123 12.5227C12.0071 12.4972 12.0042 12.4778 12.0034 12.4721C12.0011 12.4564 12.0001 12.4462 11.9998 12.4436C11.9991 12.4365 11.9995 12.4381 11.9998 12.4473C12.0003 12.4656 12.0003 12.5037 11.9945 12.5544C11.9827 12.6586 11.9503 12.7886 11.8754 12.9129C11.7692 13.089 11.4095 13.5375 10.0408 13.5375V15.5375C11.9075 15.5375 13.0264 14.8776 13.5883 13.9454C13.8474 13.5154 13.9465 13.0913 13.9818 12.7798C13.9996 12.6227 14.002 12.4876 13.9988 12.3847C13.9972 12.3333 13.9941 12.2872 13.9902 12.2477C13.9883 12.2288 13.9858 12.2062 13.9822 12.1821C13.9808 12.1723 13.9774 12.1497 13.9717 12.122C13.9698 12.1124 13.9624 12.0764 13.9489 12.0317C13.9441 12.0157 13.9257 11.9545 13.8899 11.8814C13.868 11.8393 13.7998 11.7319 13.7502 11.6693C13.6439 11.5628 13.2679 11.3597 12.9915 11.3208V13.3208ZM10.0408 13.5375C9.30305 13.5375 8.58044 13.2774 8.00962 12.9834C7.73057 12.8397 7.50347 12.696 7.34846 12.59C7.27131 12.5372 7.21302 12.4944 7.17616 12.4666C7.15775 12.4527 7.14477 12.4426 7.13753 12.4369C7.13392 12.434 7.13175 12.4323 7.13106 12.4317C7.13071 12.4314 7.13074 12.4315 7.13115 12.4318C7.13135 12.432 7.13165 12.4322 7.13204 12.4325C7.13224 12.4327 7.13245 12.4329 7.1327 12.4331C7.13282 12.4331 7.13302 12.4333 7.13308 12.4334C7.13329 12.4335 7.1335 12.4337 6.5 13.2074C5.8665 13.9812 5.86672 13.9814 5.86696 13.9816C5.86704 13.9816 5.86728 13.9818 5.86745 13.982C5.86779 13.9822 5.86816 13.9825 5.86855 13.9829C5.86934 13.9835 5.87023 13.9842 5.87122 13.985C5.8732 13.9866 5.87559 13.9886 5.87838 13.9908C5.88396 13.9953 5.89114 14.001 5.89989 14.0079C5.91739 14.0217 5.94118 14.0402 5.97093 14.0626C6.03039 14.1075 6.11399 14.1687 6.21908 14.2406C6.42854 14.3839 6.72734 14.5727 7.09394 14.7615C7.81445 15.1326 8.86225 15.5375 10.0408 15.5375V13.5375Z" fill="#0B0B0C"/>
    <path d="M9.75 4.96118L9.75 16.1335" stroke="#0B0B0C" stroke-width="2" stroke-linecap="round"/>
  </symbol>
  <symbol id="cc-icon" viewBox="0 0 21 16" fill="none">
    <path
    d="M17.3636 1H2.63636C1.73262 1 1 1.7835 1 2.75V13.25C1 14.2165 1.73262 15 2.63636 15H17.3636C18.2674 15 19 14.2165 19 13.25V2.75C19 1.7835 18.2674 1 17.3636 1Z"
    stroke="#0B0B0C" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
    <path d="M1 6L19 6" stroke="#0B0B0C" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
    <path d="M13 11H15" stroke="#0B0B0C" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
  </symbol>
  <symbol id="affirm" viewBox="0 0 40 22" fill="none">
    <title id="shopify-payment-terms-modal-affirm">Affirm</title>
    <path fill="#000000"
      d="M3.058 14.543c-.482 0-.724-.236-.724-.623 0-.72.812-.965 2.292-1.121 0 .962-.656 1.744-1.569 1.744zm.638-5.413c-1.058 0-2.275.495-2.936 1.017l.604 1.26c.53-.48 1.386-.892 2.159-.892.734 0 1.14.243 1.14.734 0 .33-.269.497-.777.563C1.99 12.056.5 12.575.5 14.026c0 1.15.826 1.846 2.116 1.846.92 0 1.74-.507 2.129-1.176v.99H6.46v-4.148c0-1.712-1.2-2.408-2.764-2.408zM20.92 9.318v6.367h1.837v-3.068c0-1.458.89-1.886 1.51-1.886.243 0 .57.07.785.23l.334-1.684a2.104 2.104 0 00-.822-.147c-.944 0-1.538.415-1.929 1.258v-1.07h-1.714zM33.899 9.13c-.971 0-1.697.57-2.075 1.118-.35-.709-1.093-1.118-1.983-1.118-.971 0-1.643.535-1.954 1.15v-.962h-1.77v6.367h1.838v-3.277c0-1.177.621-1.742 1.201-1.742.525 0 1.007.337 1.007 1.207v3.812h1.835v-3.277c0-1.19.606-1.742 1.213-1.742.486 0 .998.35.998 1.193v3.826h1.834v-4.401c0-1.431-.971-2.154-2.144-2.154zM16.452 9.317h-1.664v-.648c0-.842.485-1.083.903-1.083.462 0 .822.203.822.203l.566-1.284s-.573-.372-1.617-.372c-1.173 0-2.508.656-2.508 2.716v.468h-2.785v-.648c0-.842.485-1.083.903-1.083.238 0 .557.054.822.203l.566-1.284c-.338-.196-.88-.372-1.617-.372-1.173 0-2.508.656-2.508 2.716v.468H7.269v1.405h1.066v4.963h1.834v-4.963h2.785v4.963h1.834v-4.963h1.664V9.317zM17.547 15.684h1.832V9.317h-1.832v6.367z" />
    <path fill="#5A31F4"
      d="M28.24.434c-4.956 0-9.372 3.413-10.625 7.8h1.795C20.457 4.968 24.012 2.1 28.24 2.1c5.14 0 9.582 3.882 9.582 9.925 0 1.356-.177 2.58-.513 3.66h1.743l.017-.059c.286-1.115.431-2.326.431-3.6 0-6.74-4.95-11.59-11.26-11.59z" />
  </symbol>
  <symbol id="squiggle" viewBox="0 0 43 10" fill="none">
    <path
      d="M9.62095 5.04617C8.94881 5.25492 8.31699 5.39409 7.71878 5.63067C5.84572 6.36986 4.08993 7.39479 2.50965 8.6715C2.22013 8.91561 1.9685 9.20426 1.76356 9.52739C1.64079 9.72655 1.45521 9.87557 1.23834 9.94914C1.02147 10.0227 0.786683 10.0163 0.573866 9.93098C0.150414 9.81964 0.0159848 9.46476 0.0025419 9.03334C-0.0117113 8.78028 0.0341362 8.52746 0.136075 8.29699C0.238015 8.06651 0.392942 7.86541 0.587309 7.71124C1.44731 6.98191 2.34482 6.30131 3.27589 5.67243C4.88859 4.53384 6.69839 3.72817 8.60601 3.2996C9.16432 3.2139 9.72969 3.18825 10.2931 3.22306C10.5766 3.23472 10.8466 3.35146 11.0539 3.552C11.2611 3.75254 11.3919 4.02352 11.4223 4.31553C11.4514 4.96274 11.4401 5.61122 11.3887 6.25693C11.3887 6.47264 11.3416 6.68835 11.2946 7.03628C11.5734 6.98943 11.8463 6.91006 12.1079 6.79969C13.1228 6.23606 14.1243 5.6933 15.1191 5.04617C16.7687 3.80877 18.5394 2.75456 20.4022 1.90096C21.4062 1.47852 22.4848 1.27937 23.568 1.31645C23.7541 1.30668 23.9402 1.33708 24.1144 1.40573C24.2886 1.47438 24.447 1.57978 24.5798 1.71523C24.7125 1.85068 24.8166 2.01322 24.8854 2.19254C24.9542 2.37186 24.9862 2.56402 24.9795 2.75685C24.9795 3.30656 24.9123 3.85628 24.8719 4.46166C24.9997 4.3712 25.1542 4.27378 25.3088 4.17636C26.7002 3.24394 28.0646 2.2628 29.4963 1.393C30.9457 0.454744 32.6266 -0.028641 34.3358 0.00131229C35.3238 0.0361044 35.8078 0.50232 35.8078 1.51129C35.8195 1.97782 35.7835 2.44432 35.7002 2.90297C35.5848 3.41514 35.4389 3.91943 35.2633 4.41295C35.5053 4.26682 35.6666 4.18332 35.8145 4.0859C37.5361 2.791 39.3752 1.67239 41.3059 0.745863C41.5215 0.653045 41.7472 0.587624 41.9781 0.551028C42.1749 0.499154 42.3835 0.528135 42.5601 0.6319C42.7368 0.735666 42.8678 0.906168 42.9258 1.1077C43.0097 1.31745 43.0229 1.55039 42.9631 1.76883C42.9033 1.98728 42.7742 2.17848 42.5964 2.31151C42.5112 2.38001 42.4187 2.43837 42.3209 2.48547C40.393 3.40943 38.5543 4.52081 36.8294 5.80463C36.453 6.05514 36.0766 6.30564 35.6868 6.54223C35.4484 6.71142 35.1882 6.84495 34.9138 6.93886C33.4082 7.32853 32.5882 6.93886 32.8638 5.28971C32.9948 4.73065 33.1702 4.18373 33.388 3.65448C33.5762 3.11868 33.7913 2.5968 34.0602 1.93575C33.5628 2.02621 33.1864 2.07492 32.8302 2.17234C31.1747 2.70218 29.6179 3.5186 28.226 4.58691C27.1035 5.37321 26.0146 6.20126 24.8652 6.9319C24.4142 7.18452 23.9125 7.32507 23.3999 7.34244C23.2503 7.3709 23.0962 7.3608 22.9512 7.31303C22.8062 7.26527 22.6747 7.18131 22.5685 7.06855C22.4622 6.9558 22.3844 6.81771 22.3418 6.66646C22.2993 6.51521 22.2933 6.35545 22.3245 6.20126C22.4231 5.49335 22.5782 4.79511 22.7883 4.11374C22.8992 3.78799 23.0294 3.46962 23.1781 3.16044C22.6262 3.27341 22.0843 3.43401 21.5583 3.64057C20.2543 4.33641 18.9571 5.03225 17.7001 5.72809C16.3559 6.54223 15.0116 7.44682 13.6673 8.28183C12.8993 8.75227 12.0091 8.96383 11.1198 8.88721C10.8941 8.89799 10.6688 8.85872 10.459 8.77204C10.2491 8.68537 10.0595 8.55327 9.903 8.38461C9.74644 8.21596 9.6265 8.01462 9.5512 7.79409C9.4759 7.57355 9.44697 7.3389 9.46636 7.10586C9.48652 6.50048 9.54702 5.83943 9.62095 5.04617Z"
      fill="#BDADFB" />
  </symbol>
  <symbol id="close" viewBox="0 0 14 14" fill="none">
    <path fill-rule="evenodd" clip-rule="evenodd" d="M8.547 6.77l5.322-5.086A.468.468 0 0014 1.347a.662.662 0 00-.131-.379l-.875-.842A.725.725 0 0012.6 0c-.175 0-.262.042-.35.126L7 5.136 1.75.127A.508.508 0 001.4 0c-.175 0-.306.042-.394.126L.131.968a.662.662 0 00-.131.38c0 .126.044.252.175.336L5.453 6.73.13 11.816a.468.468 0 00-.131.337c0 .126.044.252.131.379l.875.842a.724.724 0 00.394.126c.175 0 .263-.042.35-.126L7 8.364l5.25 5.01a.508.508 0 00.35.126c.175 0 .306-.042.394-.126l.875-.842a.662.662 0 00.131-.38.378.378 0 00-.175-.336L8.547 6.77z" fill="#000000"/>
  </symbol>
</svg>`;
}

/**
 *
 * @param {string} storeUrl The URL of the store
 * @param {string} modalToken A token generated by the modal to uniquely identify the modal and actions taken by the user.
 * @param {string} variantInfo Variant information in the shape of `variantId:quantity`, is undefined when opened on the cart page
 * @param {Cart} cart Details related to the cart that the modal is opened from (undefined on product page)
 * @returns {string} HTML to display a Shop Pay Button with the text Continue to Checkout
 */
export function getContinueToCheckoutButtonHtml(
  storeUrl: string,
  modalToken: string,
  variantInfo?: VariantModalDetails,
  cart?: Cart,
): string {
  const variantsAttribute =
    variantInfo === undefined
      ? ''
      : `variants="${variantInfo.idQuantityMapping}" ${
          variantInfo.available === false ? 'disabled' : ''
        }`;

  const cartAttribute =
    cart === undefined
      ? ''
      : `cart=${encodeURIComponent(
          JSON.stringify({
            currency: cart.currency,
            items: cart.items,
            token: cart.token,
          }),
        )}`;

  return `
 <shop-pay-button style="width: 100%; height: 50px; --shop-pay-button-width: auto;" store-url="${storeUrl}" ${variantsAttribute} button-text="" class="shop-pay-continue-to-checkout-button" payment-option="shop_pay_installments" source="installments_modal" source-token="${modalToken}" redirect-source="installments_modal" ${cartAttribute}></shop-pay-button>
`;
}

/**
 * Extracts a fontFace declaration object for the specified fontFamily
 * @param {string} fontFamily string
 * @returns {FontFaceDto[] | null} FontFaceDescriptors object
 */
export function extractFontFaceDeclarationForFontFamily(fontFamily: string) {
  if (!fontFamily || fontFamily.trim() === '') {
    return [];
  }

  const isFontFaceRule = (rule: CSSRule) => {
    // in Jest there is no CSSFontFaceRule class, so we should rely on another way to check the type
    return rule.constructor.name === 'CSSFontFaceRule';
  };

  try {
    const fontFaceList = [...document.styleSheets]
      /**
       * Some stylesheets are not accessible due to CORS policy, so probing each for availability
       */
      .filter((stylesheet) => {
        try {
          return Boolean(stylesheet.cssRules);
        } catch {
          return false;
        }
      })
      /**
       * We are looking only into font-face at-rule
       */
      .flatMap(
        (stylesheet) =>
          [...stylesheet.cssRules].filter(isFontFaceRule) as CSSFontFaceRule[],
      )
      /**
       * Looking for specific font-face only
       */
      .filter((font) =>
        fontFamily.includes(font.style.getPropertyValue('font-family')),
      )
      /**
       * Reducing CSSFontFaceRule to correspond to the FontFaceDescriptors
       */
      .map((font: CSSFontFaceRule) => {
        const obj: Record<string, string> = {};
        for (let i = font.style.length; i--; ) {
          const key = font.style[i];
          obj[key] = font.style.getPropertyValue(key);
        }

        // normalize URL
        const src = font.style.getPropertyValue('src');
        if (src) {
          obj.src = src.replace(
            /url\((["']?)([^"')]+)\1\)/gm,
            (_match: string, _quote: string, url: string) => {
              if (url.match(/^(https?:)?\/\//)) {
                return _match;
              } else {
                const baseURL = font.parentStyleSheet?.href ?? location.href;
                const parsedURL = new URL(url, baseURL);

                return `url("${parsedURL.toString()}")`;
              }
            },
          );
        }

        return obj;
      });

    /**
     * Builds FontFaceDescriptors object from the CSSFontFaceRule object
     * @param fontFace
     * @returns {FontFaceDescriptors} - FontFace Descriptors
     */
    const buildFontFaceDescriptor = (fontFace: Record<string, string>) => {
      if (!fontFace) {
        return {};
      }

      const fontFaceRuleDeclarationMap = new Map<
        string,
        keyof FontFaceDescriptors
      >([
        ['font-weight', 'weight'],
        ['font-display', 'display'],
        ['font-style', 'style'],
        ['font-stretch', 'stretch'],
        ['ascent-override', 'ascentOverride'],
        ['descent-override', 'descentOverride'],
        ['font-feature-settings', 'featureSettings'],
        ['line-gap-override', 'lineGapOverride'],
        ['unicode-range', 'unicodeRange'],
      ]);

      const fontFaceDescriptors = {} as Record<string, string>;
      fontFaceRuleDeclarationMap.forEach((value, key) => {
        if (fontFace[key]) {
          fontFaceDescriptors[value] = fontFace[key];
        }
      });

      return fontFaceDescriptors as FontFaceDescriptors;
    };

    return (fontFaceList ?? []).map(
      (fontFace) =>
        ({
          src: fontFace.src,
          fontFamily: fontFace['font-family'].replace(/["']/g, ''),
          fontFaceDescriptors: buildFontFaceDescriptor(fontFace),
        }) as FontFaceDto,
    );
  } catch (error) {
    // inform error
    return [];
  }
}

/**
 * Checks if the prequalified amount feature is enabled.
 *
 * Before rolling it out to the public, we may check it within the group.
 * @param flagName
 * @returns {boolean} if feature is enabled
 */
export function isPrequalifiedAmountFeatureEnabled(
  flagName = 'force_prequal_amount',
) {
  return Boolean(
    getQueryParam(flagName) === 'true' || getCookie(flagName) === 'true',
  );
}
